import {Observable, ReplaySubject, Scheduler} from "rxjs";
import {SecurityTradingSystemIndicators} from "../models/SecurityTradingSystemIndicators";
import {TradingSystemsService} from "./TradingSystemsService";
import {orderIndicators} from "./TradingSystemsUtil";
import {SelectedTradingSystem} from "../models/SelectedTradingSystem";
import {SelectedSystemType} from "../models/SelectedSystemType";
import {LocalStorageService} from "../../types/NgStorage";
import {ObjectUtil} from "../../js/util/ObjectUtil";

const PRICE_FILTERS_LOCAL_STORAGE_KEY = "trading-systems-price-filter";
const DEFAULT_PRICE_FILTERS: PriceRangeFilter = {
    above: 15,
    below: 200
};

export interface PriceRangeFilter {
    readonly above: number;
    readonly below: number;
}

export class TradingSystemsViewStore {

    private readonly tradingSystems$ = new ReplaySubject<Array<SecurityTradingSystemIndicators>>(1);
    private readonly filteredTradingSystems$ = new ReplaySubject<Array<SecurityTradingSystemIndicators>>(1);
    private readonly selected$ = new ReplaySubject<SelectedTradingSystem>(1);
    private readonly priceRangeFilter$ = new ReplaySubject<PriceRangeFilter>(1);

    public constructor(private service: TradingSystemsService,
                       private $localStorage: LocalStorageService) {
        this.defaultLocalStorage();
        this.init();
    }

    private defaultLocalStorage() {
        this.$localStorage.$default({
                                        [PRICE_FILTERS_LOCAL_STORAGE_KEY]: DEFAULT_PRICE_FILTERS
                                    });
        const priceRangeFilter = this.$localStorage[PRICE_FILTERS_LOCAL_STORAGE_KEY];

        this.priceRangeFilter$.next(priceRangeFilter);
    }

    private init() {
        this.updateList();
        this.watchFilters();
    }

    private updateList() {
        const response = this.service.list();
        Observable.fromPromise(response)
                  .map((indicators) => indicators.sort(orderIndicators()))
                  .subscribe((indicators) => this.tradingSystems$.next(indicators));
    }

    private watchFilters() {
        Observable.combineLatest(this.tradingSystems$, this.priceRangeFilter$)
                  .takeUntil(this.selected$)
                  .map(([tradingSystems, priceRangeFilter]) => this.applyFilters(tradingSystems, priceRangeFilter))
                  .subscribe((filteredTradingSystems) => this.filteredTradingSystems$.next(filteredTradingSystems));
    }

    public get tradingSystems(): Observable<Array<SecurityTradingSystemIndicators>> {
        return this.filteredTradingSystems$.subscribeOn(Scheduler.async);
    }

    public select(system: SecurityTradingSystemIndicators, type: SelectedSystemType) {
        const selected = {
            type,
            system
        };
        this.selected$.next(selected);
    }

    public get selected(): Observable<SelectedTradingSystem> {
        return this.selected$.subscribeOn(Scheduler.async);
    }

    public get priceRangeFilter(): Observable<PriceRangeFilter> {
        return this.priceRangeFilter$.subscribeOn(Scheduler.async);
    }

    public updatePriceRangeFilter(filter: PriceRangeFilter): void {
        const filterCopy = ObjectUtil.copy(filter);
        this.priceRangeFilter$.next(filterCopy);
        this.$localStorage[PRICE_FILTERS_LOCAL_STORAGE_KEY] = filterCopy;
    }

    public dispose() {
        this.tradingSystems$.complete();
        this.selected$.complete();
    }

    private applyFilters(tradingSystems: Array<SecurityTradingSystemIndicators>,
                         priceRangeFilter: PriceRangeFilter): Array<SecurityTradingSystemIndicators> {
        return tradingSystems.filter((tsi) => tsi.code === "XJO" || tsi.statistics.price >= priceRangeFilter.above && tsi.statistics.price <= priceRangeFilter.below);
    }
}
